import { logger } from '@/shared/utils';
import { IS_CHAT_STORE_IN_DB_ENABLED } from '@/processENV';
import dbConnectionManager from './DbConnectionManager';

const RECENT_CHATS = 'recent_chats';

class RecentChatIdb {
   constructor() {
      RecentChatIdb.instance = this;
   }

   async getConnection() {
      return dbConnectionManager.getConnection();
   }

   async getRecentChats({ messageTime = null, limit = null, direction = 'backward' } = {}) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return [];
      try {
         const db = await this.getConnection();
         return new Promise((resolve, reject) => {
            const transaction = db.transaction([RECENT_CHATS], 'readonly');
            const objectStore = transaction.objectStore(RECENT_CHATS);
            const index = objectStore.index('timestamp');
            const results = [];

            let range = null;
            if (messageTime) {
               // Handle potential microsecond vs millisecond timestamp mismatch
               const timestampBound = messageTime > 9999999999999 ? Math.floor(messageTime / 1000) : messageTime;
               range =
                  direction === 'backward'
                     ? IDBKeyRange.upperBound(timestampBound, true)
                     : IDBKeyRange.lowerBound(timestampBound, true);
            }

            const cursorDirection = direction === 'backward' ? 'prev' : 'next';
            const request = index.openCursor(range, cursorDirection);

            request.onsuccess = event => {
               const cursor = event.target.result;
               if (cursor) {
                  results.push(cursor.value);
                  if (limit && results.length >= limit) {
                     logger.log(`Fetched ${results.length} chats from DB sorted by timestamp.`);
                     resolve(results);
                  } else {
                     cursor.continue();
                  }
               } else {
                  logger.log(`Fetched ${results.length} chats from DB sorted by timestamp.`);
                  resolve(results);
               }
            };

            request.onerror = event => {
               logger.error('getRecentChats error:', event.target.error);
               reject(event.target.error);
            };
         });
      } catch (error) {
         logger.error('error getRecentChats catch:', error);
         return [];
      }
   }

   async setRecentChats(chats) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return;
      try {
         logger.log(`Attempting to save ${chats.length} chats to DB`);
         const db = await this.getConnection();
         return new Promise((resolve, reject) => {
            const transaction = db.transaction([RECENT_CHATS], 'readwrite');
            const objectStore = transaction.objectStore(RECENT_CHATS);

            chats.forEach(chat => {
               const key = chat.fromUserId || chat.groupId || chat.userId;
               if (key) {
                  // Ensure required indexing fields are present
                  if (!chat.timestamp) {
                     chat.timestamp = Date.now();
                  }
                  if (!chat.messageTime) {
                     // Fallback for messageTime: timestamp * 1000
                     chat.messageTime = chat.timestamp * 1000;
                  }
                  objectStore.put(chat, key);
               } else {
                  logger.warn('Skipping chat with no key:', chat);
               }
            });

            transaction.oncomplete = () => {
               logger.log('setRecentChats transaction complete');
               resolve();
            };

            transaction.onerror = event => {
               logger.error('setRecentChats transaction error:', event.target.error);
               reject(event.target.error);
            };
         });
      } catch (error) {
         logger.error('error setRecentChats', error);
      }
   }

   async updateRecentChat(chatId, chat) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return;
      try {
         const db = await this.getConnection();
         return new Promise((resolve, reject) => {
            const transaction = db.transaction([RECENT_CHATS], 'readwrite');
            const objectStore = transaction.objectStore(RECENT_CHATS);

            // Ensure required indexing fields are present
            if (!chat.timestamp) {
               chat.timestamp = Date.now();
            }
            if (!chat.messageTime) {
               // Fallback for messageTime: timestamp * 1000
               chat.messageTime = chat.timestamp * 1000;
            }

            const request = objectStore.put(chat, chatId);

            request.onsuccess = () => {
               resolve(request.result);
            };

            request.onerror = event => {
               logger.error('updateRecentChat error:', event.target.error);
               reject(event.target.error);
            };
         });
      } catch (error) {
         logger.error('error updateRecentChat', error);
      }
   }

   async deleteRecentChat(chatId) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return;
      try {
         const db = await this.getConnection();
         return new Promise((resolve, reject) => {
            const transaction = db.transaction([RECENT_CHATS], 'readwrite');
            const objectStore = transaction.objectStore(RECENT_CHATS);
            const request = objectStore.delete(chatId);

            request.onsuccess = () => {
               resolve(request.result);
            };

            request.onerror = event => {
               logger.error('deleteRecentChat error:', event.target.error);
               reject(event.target.error);
            };
         });
      } catch (error) {
         logger.error('error deleteRecentChat', error);
      }
   }

   async patchRecentChat(chatId, partialUpdate) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return;
      try {
         const db = await this.getConnection();
         const transaction = db.transaction([RECENT_CHATS], 'readwrite');
         const objectStore = transaction.objectStore(RECENT_CHATS);

         const existing = await new Promise((resolve, reject) => {
            const request = objectStore.get(chatId);
            request.onsuccess = event => resolve(event.target.result);
            request.onerror = event => reject(event.target.error);
         });

         const updated = { ...(existing || {}), ...partialUpdate };

         await new Promise((resolve, reject) => {
            const request = objectStore.put(updated, chatId);
            request.onsuccess = () => resolve();
            request.onerror = event => reject(event.target.error);
         });
      } catch (error) {
         logger.error('error patchRecentChat', error);
      }
   }

   async getRecentChatById(chatId) {
      if (!IS_CHAT_STORE_IN_DB_ENABLED) return null;
      try {
         const db = await this.getConnection();
         return new Promise((resolve, reject) => {
            const transaction = db.transaction([RECENT_CHATS], 'readonly');
            const objectStore = transaction.objectStore(RECENT_CHATS);
            const request = objectStore.get(chatId);

            request.onsuccess = event => {
               resolve(event.target.result);
            };

            request.onerror = event => {
               logger.error('getRecentChatById error:', event.target.error);
               reject(event.target.error);
            };
         });
      } catch (error) {
         logger.error('error getRecentChatById catch:', error);
         return null;
      }
   }
}

const recentChatIdb = new RecentChatIdb();

if (typeof window !== 'undefined') {
   window.inspectRecentChats = async () => {
      const chats = await recentChatIdb.getRecentChats();
      logger.log(`[RecentChatIdb] Found ${chats.length} recent chats`);
      if (chats.length > 0) {
         logger.debug(chats);
      }
      return chats;
   };

   window.inspectRecentChatById = async chatId => {
      const chat = await recentChatIdb.getRecentChatById(chatId);
      if (chat) {
         logger.log(`[RecentChatIdb] Found chat for ${chatId}:`, chat);
      } else {
         logger.log(`[RecentChatIdb] No chat found for ${chatId}`);
      }
      return chat;
   };
}

export default recentChatIdb;
